import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Chat,
  Visibility,
  Warning,
  Send,
  Email,
  Person,
  ShoppingCart,
} from '@mui/icons-material';
import { useGetChatsQuery, useGetChatMessagesQuery, useSendAdminContactMutation } from '../../store/api/adminApi';

const ChatsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedChat, setSelectedChat] = useState<any>(null);
  const [chatDialog, setChatDialog] = useState(false);
  const [contactDialog, setContactDialog] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [contactForm, setContactForm] = useState({
    recipientType: 'both' as 'buyer' | 'seller' | 'both',
    subject: '',
    message: '',
  });

  const { data: chatsData, isLoading, error, refetch } = useGetChatsQuery({
    page: page + 1,
    limit: rowsPerPage,
    showExcluded: showExcluded ? 'true' : 'false',
  } as any);

  const { data: messagesData, isLoading: messagesLoading, refetch: refetchMessages } = useGetChatMessagesQuery(
    { chatId: selectedChat?.id || '', limit: 50 },
    { skip: !selectedChat?.id }
  );

  const [sendAdminContact, { isLoading: sendingContact }] = useSendAdminContactMutation();

  const chats = (chatsData as any)?.chats || [];
  const totalChats = (chatsData as any)?.total || 0;
  const messages = (messagesData as any)?.messages || [];
  const chatInfo = (messagesData as any)?.chatInfo || null;

  // デバッグ用：chatInfoの内容を確認
  React.useEffect(() => {
    if (chatInfo) {
      console.log('=== チャット情報デバッグ ===');
      console.log('chatInfo全体:', chatInfo);
      console.log('購入者情報:', chatInfo.buyer);
      console.log('出品者情報:', chatInfo.seller);
      console.log('購入者メール:', chatInfo.buyer?.email);
      console.log('出品者メール:', chatInfo.seller?.email);
      console.log('=======================');
    }
  }, [chatInfo]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewChat = (chat: any) => {
    setSelectedChat(chat);
    setChatDialog(true);
    // API キャッシュをクリアして最新データを取得
    refetchMessages();
  };

  const handleOpenContactDialog = () => {
    setContactDialog(true);
  };

  const handleCloseContactDialog = () => {
    setContactDialog(false);
    setContactForm({
      recipientType: 'both',
      subject: '',
      message: '',
    });
  };

  const handleSendContact = async () => {
    if (!selectedChat?.id || !contactForm.subject || !contactForm.message) {
      alert('件名とメッセージを入力してください');
      return;
    }

    try {
      await sendAdminContact({
        chatId: selectedChat.id,
        recipientType: contactForm.recipientType,
        subject: contactForm.subject,
        message: contactForm.message,
      }).unwrap();

      alert('連絡を送信しました');
      handleCloseContactDialog();
      refetchMessages(); // メッセージリストを再取得
    } catch (error) {
      console.error('連絡送信エラー:', error);
      alert('連絡の送信に失敗しました');
    }
  };

  const getStatusChip = (chat: any) => {
    // キャンセルリクエスト中
    if (chat.cancelReqFrom) {
      return <Chip label="キャンセルリクエスト中" color="warning" size="small" icon={<Warning />} />;
    }
    
    // 完了（isDoneがtrue）
    if (chat.isDone) {
      return <Chip label="完了" color="success" size="small" />;
    }
    
    // 受け取り済み（評価待ち）
    if (chat.isReceived) {
      return <Chip label="受け取り済み" color="info" size="small" />;
    }
    
    // 発送済み
    if (chat.isDispatched) {
      return <Chip label="発送済み" color="info" size="small" />;
    }
    
    // 支払い完了（発送待ち）
    if (chat.isPaymentCompleted) {
      return <Chip label="支払い完了" color="primary" size="small" />;
    }
    
    // 振込完了マーク済み（承認待ち）- isPaymentMarkedAsSent または isPaymentMaredAsSent がtrue
    if (chat.isPaymentMarkedAsSent || chat.isPaymentMaredAsSent) {
      return <Chip label="承認待ち" color="warning" size="small" />;
    }
    
    // 支払い待ち（購入者がいる場合）
    if (chat.buyer) {
      return <Chip label="支払い待ち" color="warning" size="small" />;
    }
    
    // 取引なし（購入者がいない場合）
    return <Chip label="取引なし" color="default" size="small" />;
  };

  const getRecipientTypeLabel = (type: string) => {
    switch (type) {
      case 'buyer': return '購入者のみ';
      case 'seller': return '出品者のみ';
      case 'both': return '購入者・出品者の両方';
      default: return type;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        チャットデータの読み込みに失敗しました
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        チャット管理
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        ユーザー間のチャット内容を監視し、不適切なやり取りがないかを確認できます。
      </Alert>

      {/* フィルター */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showExcluded}
              onChange={(e) => {
                setShowExcluded(e.target.checked);
                setPage(0);
              }}
            />
          }
          label="統計除外のチャットも表示"
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>チャットID</TableCell>
              <TableCell>参加者</TableCell>
              <TableCell>商品</TableCell>
              <TableCell>メッセージ数</TableCell>
              <TableCell>最終メッセージ</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>アクション</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {chats.map((chat: any) => (
              <TableRow key={chat.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {chat.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      購入者: {chat.buyerName || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      出品者: {chat.sellerName || 'N/A'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {chat.productName || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {chat.messageCount || 0}件
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {chat.lastMessageAt 
                      ? new Date(chat.lastMessageAt).toLocaleDateString('ja-JP')
                      : 'N/A'
                    }
                  </Typography>
                </TableCell>
                <TableCell>{getStatusChip(chat)}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => handleViewChat(chat)}
                  >
                    内容確認
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalChats}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* チャット詳細ダイアログ */}
      <Dialog
        open={chatDialog}
        onClose={() => setChatDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Chat sx={{ mr: 1 }} />
            チャット内容確認
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedChat && (
            <Box>
              <Typography variant="h6" gutterBottom>
                基本情報
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Box display="flex" gap={2} mb={2}>
                  <Card variant="outlined" sx={{ flex: 1 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Person sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          購入者情報
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        名前: {chatInfo?.buyer?.name || selectedChat.buyerName || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        メール: {chatInfo?.buyer?.email && chatInfo.buyer.email.trim() !== '' ? chatInfo.buyer.email : 'メールアドレス未設定'}
                      </Typography>
                    </CardContent>
                  </Card>
                  
                  <Card variant="outlined" sx={{ flex: 1 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Person sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          出品者情報
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        名前: {chatInfo?.seller?.name || selectedChat.sellerName || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        メール: {chatInfo?.seller?.email && chatInfo.seller.email.trim() !== '' ? chatInfo.seller.email : 'メールアドレス未設定'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
                
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <ShoppingCart sx={{ mr: 1 }} />
                      <Typography variant="subtitle1" fontWeight="bold">
                        商品情報
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      商品名: {chatInfo?.productName || selectedChat.productName || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      商品ID: {chatInfo?.productId || 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  メッセージ履歴
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Email />}
                  onClick={handleOpenContactDialog}
                  color="primary"
                >
                  連絡する
                </Button>
              </Box>
              
              {messagesLoading ? (
                <CircularProgress />
              ) : (
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {messages.length > 0 ? (
                    messages.map((message: any, index: number) => (
                      <ListItem key={index} divider>
                        <Box display="flex" alignItems="flex-start" width="100%">
                          <Avatar sx={{ mr: 2, bgcolor: message.userId === 'admin' ? 'primary.main' : 'grey.500' }}>
                            {message.senderName?.[0] || '?'}
                          </Avatar>
                          <Box flex={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography 
                                variant="subtitle2" 
                                fontWeight="bold"
                                color={message.userId === 'admin' ? 'primary' : 'inherit'}
                              >
                                {message.senderName || 'Unknown'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {new Date(message.createdAt).toLocaleString('ja-JP')}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                              {message.message || message.content || message.text}
                            </Typography>
                            {message.imageUrl && (
                              <Box mt={1}>
                                <img 
                                  src={message.imageUrl} 
                                  alt="添付画像"
                                  style={{ maxWidth: '200px', maxHeight: '200px' }}
                                />
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      メッセージがありません
                    </Typography>
                  )}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChatDialog(false)}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 連絡ダイアログ */}
      <Dialog
        open={contactDialog}
        onClose={handleCloseContactDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Email sx={{ mr: 1 }} />
            ユーザーへの連絡
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>送信先</InputLabel>
              <Select
                value={contactForm.recipientType}
                onChange={(e) => setContactForm({ ...contactForm, recipientType: e.target.value as any })}
                label="送信先"
              >
                <MenuItem value="buyer">購入者のみ</MenuItem>
                <MenuItem value="seller">出品者のみ</MenuItem>
                <MenuItem value="both">購入者・出品者の両方</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              margin="normal"
              label="件名"
              value={contactForm.subject}
              onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
              required
            />
            
            <TextField
              fullWidth
              margin="normal"
              label="メッセージ"
              multiline
              rows={6}
              value={contactForm.message}
              onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
              required
            />
            
            <Alert severity="info" sx={{ mt: 2 }}>
              この連絡は対象ユーザーにプッシュ通知として送信され、チャット内にも管理者メッセージとして表示されます。
              送信先: {getRecipientTypeLabel(contactForm.recipientType)}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContactDialog}>
            キャンセル
          </Button>
          <Button
            onClick={handleSendContact}
            variant="contained"
            disabled={sendingContact || !contactForm.subject || !contactForm.message}
            startIcon={sendingContact ? <CircularProgress size={20} /> : <Send />}
          >
            送信
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatsPage; 